/**
 * MDM Cache Service
 *
 * Purpose: Centralized caching layer for MDM values with tenant-scoped lookups
 * Architecture: Wraps mdmService with React Query for caching and invalidation
 *
 * Features:
 * - Tenant-scoped caching (caches ALL values including inactive/deleted)
 * - Client-side filtering for active-only vs all values
 * - React Query hooks for components
 * - Preloading utilities for common fields
 * - Future: Automatic cache invalidation via WebSocket
 *
 * Cache Strategy:
 * - Cache stores ALL values (include_inactive=true, include_deleted=true)
 * - Display lookups (useMDMLookup) filter to active values only
 * - Admin views (useMDMValuesFiltered) can apply custom filters client-side
 * - Single source of truth - no duplicate caches
 *
 * Author: Development Team
 * Created: 2025-11-30
 */

import {
  useQuery,
  useQueries,
  useQueryClient,
  keepPreviousData,
  UseQueryResult,
} from '@tanstack/react-query';
import { ALL_LABEL, ALL_VALUE } from '../utils/listValues';
import { useMemo, useCallback, useEffect } from 'react';
import { mdmService } from './mdmService';
import { MasterDataValue } from '../types/mdm';
import { PermissionKey } from '../constants/permissionKeys';

// =============================================================================
// CACHE KEY FACTORIES
// =============================================================================

export const mdmCacheKeys = {
  // All MDM cache entries
  all: () => ['mdm'] as const,

  // All values for a tenant
  tenant: (tenantId: string) => ['mdm', 'tenant', tenantId] as const,

  // Values for a specific fielddef within a tenant
  values: (fielddefId: string, tenantId: string) =>
    ['mdm', 'values', fielddefId, tenantId] as const,

  // Single value by ID
  value: (valueId: string) => ['mdm', 'value', valueId] as const,
};

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

const CACHE_CONFIG = {
  // How long data is considered fresh (won't refetch)
  staleTime: 10 * 60 * 1000, // 10 minutes

  // How long to keep unused data in cache
  gcTime: 30 * 60 * 1000, // 30 minutes

  // Fields to preload at app startup
  preloadFields: ['fd_currency'] as const,
};

// =============================================================================
// FILTER HELPERS
// =============================================================================

/**
 * Filter options for MDM values
 */
export interface MDMFilterOptions {
  /** Include inactive values (default: true for cache, false for display) */
  includeInactive?: boolean;
  /** Include deleted values (default: true for cache, false for display) */
  includeDeleted?: boolean;
  /** Filter by scope */
  scope?: 'GLOBAL' | 'LOCAL' | 'OVERRIDDEN' | 'ALL';
  /** Search term to filter by name */
  search?: string;
}

/**
 * Filter MDM values based on options
 *
 * Purpose: Client-side filtering of cached values
 * Input: values - Array of all cached values
 *        options - Filter criteria
 * Output: Filtered array of values
 */
export const filterMDMValues = (
  values: MasterDataValue[],
  options: MDMFilterOptions = {}
): MasterDataValue[] => {
  const { includeInactive = false, includeDeleted = false, scope, search } = options;

  return values.filter((value) => {
    // Filter by active status
    if (!includeInactive && !value.is_active) {
      return false;
    }

    // Filter by deleted status
    if (!includeDeleted && value.is_deleted) {
      return false;
    }

    // Filter by scope
    if (scope && scope !== 'ALL' && value.scope_info?.scope !== scope) {
      return false;
    }

    // Filter by search term
    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      if (!value.name.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Filter to only active, non-deleted values (for display lookups)
 */
export const filterActiveValues = (values: MasterDataValue[]): MasterDataValue[] => {
  return filterMDMValues(values, { includeInactive: false, includeDeleted: false });
};

// =============================================================================
// REACT QUERY HOOKS
// =============================================================================

/**
 * Hook to get ALL MDM values for a field definition (including inactive/deleted)
 *
 * Purpose: Fetch and cache ALL MDM values for a specific fielddef_id
 * Note: This is the raw cache hook. For most use cases, use useMDMValues which
 *       provides filtered results (active-only by default).
 * Input: fielddefId - The field definition ID (e.g., 'fd_organization')
 *        tenantId - The tenant context for the values
 * Output: React Query result with ALL values (including inactive/deleted)
 *
 * @internal Used internally by useMDMValues and useMDMLookup
 * @example
 * const { data: allOrganizations, isLoading } = useMDMValuesAll('fd_organization', 'GLOBAL');
 */
export const useMDMValuesAll = (
  fielddefId: string,
  tenantId: string,
  options?: { enabled?: boolean }
): UseQueryResult<MasterDataValue[], Error> => {
  return useQuery({
    ...mdmValuesQuery(fielddefId, tenantId),
    enabled: options?.enabled !== false && !!fielddefId && !!tenantId,
  });
};

/**
 * The query behind useMDMValuesAll, for callers that read several lists at once
 * (useQueries) and must share this cache: same key, same fetch, same lifetime.
 */
export const mdmValuesQuery = (fielddefId: string, tenantId: string) => ({
  queryKey: mdmCacheKeys.values(fielddefId, tenantId),
  queryFn: () =>
    mdmService.getValues({
      fielddef_id: fielddefId,
      tenant_id: tenantId,
      // Fetch ALL values - filtering happens client-side
      include_inactive: true,
      include_deleted: true,
    }),
  staleTime: CACHE_CONFIG.staleTime,
  gcTime: CACHE_CONFIG.gcTime,
});

/**
 * Hook to get MDM values for a field definition with filtering
 *
 * Purpose: Primary hook for fetching MDM values with client-side filtering
 * Input: fielddefId - The field definition ID
 *        tenantId - The tenant context for the values
 *        filterOptions - Filter criteria (includeInactive, includeDeleted, scope, search)
 *                        Defaults to active-only values (includeInactive: false, includeDeleted: false)
 * Output: Object with filtered data, loading state, error, and refetch function
 *
 * @example
 * // Get only active values (default - for forms and displays)
 * const { data: activeOrgs } = useMDMValues('fd_organization', 'GLOBAL');
 *
 * // Get all values including inactive (for admin views)
 * const { data: allOrgs } = useMDMValues('fd_organization', 'GLOBAL', {
 *   includeInactive: true,
 *   includeDeleted: false,
 * });
 */
export const useMDMValues = (
  fielddefId: string,
  tenantId: string,
  filterOptions: MDMFilterOptions = {},
  queryOptions?: { enabled?: boolean }
): {
  data: MasterDataValue[];
  allData: MasterDataValue[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} => {
  const query = useMDMValuesAll(fielddefId, tenantId, queryOptions);

  const filteredData = useMemo(() => {
    if (!query.data) return [];
    return filterMDMValues(query.data, filterOptions);
  }, [query.data, filterOptions]);

  return {
    data: filteredData,
    allData: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * What a permission lets a picker offer from a list (ORCH-332, ORCH-21 §10.11)
 *
 * Purpose: Scoped options for pickers. Labels keep coming from the shared, unfiltered
 *          cache (values already on a visible record always display); this fetch only
 *          says which ids may be offered.
 * Input: permission - from usePickerScope(); null means no permission narrowing
 *        options.offer - a picker's offer request (ORCH-366): runs even when permission
 *        is null and sends `offer=true`, so every offer-time filter applies. Without it
 *        a null permission makes no request (the MDM Manager's visibility read)
 *        options.includeAll - also inactive and deleted values (the MDM Manager lists
 *        them); pickers offer active values only
 * Output: scope - id -> selectable (false for an ancestor kept for navigation), or
 *         null when no request runs, still loading or failed; isError - the scoped fetch failed
 *         (callers must not fall back to offering every value)
 */
export const useMDMScopedOptions = (
  fielddefId: string,
  tenantId: string,
  permission: PermissionKey | null,
  options?: { enabled?: boolean; includeAll?: boolean; offer?: boolean; filterBy?: string[] }
): {
  scope: Map<string, boolean> | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
} => {
  const includeAll = !!options?.includeAll;
  // A picker's offer request runs whatever the permission (ORCH-366); the MDM Manager's
  // visibility read passes no `offer` and still needs a permission to fetch
  const offer = !!options?.offer;
  const active = offer || !!permission;
  // ORCH-400: the related-field constraints the surrounding form holds, as query terms
  const filterBy = options?.filterBy ?? [];
  const filterKey = filterBy.join('|');
  const query = useQuery({
    // Under the fielddef's key, so invalidating the fielddef refetches it too
    queryKey: [
      ...mdmCacheKeys.values(fielddefId, tenantId),
      'scoped',
      permission,
      includeAll,
      filterKey,
    ] as const,
    queryFn: () =>
      mdmService.getValues({
        fielddef_id: fielddefId,
        tenant_id: tenantId,
        for_permission: permission ?? undefined,
        ...(offer ? { offer: true } : {}),
        ...(filterBy.length ? { filter_by: filterBy } : {}),
        ...(includeAll ? { include_inactive: true, include_deleted: true } : {}),
      }),
    staleTime: CACHE_CONFIG.staleTime,
    gcTime: CACHE_CONFIG.gcTime,
    // A parent change narrows the picker without flashing it back to "Loading..."
    placeholderData: keepPreviousData,
    enabled: options?.enabled !== false && active && !!fielddefId && !!tenantId,
  });
  const scope = useMemo(
    () =>
      active && query.data ? new Map(query.data.map((v) => [v.id, v.selectable !== false])) : null,
    [active, query.data]
  );
  return {
    scope,
    isLoading: active && query.isLoading,
    isError: active && query.isError,
    refetch: query.refetch,
  };
};

/**
 * The options a scoped picker shows (ORCH-332)
 *
 * Logic: every option the permission reaches and allows selecting, plus whatever is
 *        already selected (an out-of-scope value already on the record stays shown and
 *        can be removed, but isn't offered again). Unscoped: every option.
 * Output: options in their original order, and the set of ids shown but not offered
 */
export const scopePickerOptions = (
  options: MasterDataValue[],
  scope: Map<string, boolean> | null,
  selectedIds: Iterable<string>
): { options: MasterDataValue[]; notOffered: Set<string> } => {
  if (!scope) return { options, notOffered: new Set() };
  const selected = new Set(selectedIds);
  const notOffered = new Set<string>();
  const shown = options.filter((option) => {
    if (scope.get(option.id) === true) return true;
    if (selected.has(option.id)) {
      notOffered.add(option.id);
      return true;
    }
    return false;
  });
  return { options: shown, notOffered };
};

/**
 * Hook to get a single MDM value by ID
 *
 * Purpose: Fetch and cache a single MDM value
 * Input: valueId - The UUID of the MDM value
 *        options - Optional configuration including hierarchy_path and tenant_id
 * Output: React Query result with single value
 *
 * @param valueId - The UUID of the MDM value
 * @param options - Optional parameters for the request
 */
export const useMDMValue = (
  valueId: string,
  options?: {
    include_hierarchy_path?: boolean;
    tenant_id?: string;
    enabled?: boolean;
  }
): UseQueryResult<MasterDataValue, Error> => {
  return useQuery({
    queryKey: mdmCacheKeys.value(valueId),
    queryFn: () =>
      mdmService.getValue(valueId, {
        include_hierarchy_path: options?.include_hierarchy_path,
        tenant_id: options?.tenant_id,
      }),
    staleTime: CACHE_CONFIG.staleTime,
    gcTime: CACHE_CONFIG.gcTime,
    enabled: options?.enabled !== false && !!valueId,
  });
};

// =============================================================================
// LOOKUP HOOK (PRIMARY INTERFACE FOR COMPONENTS)
// =============================================================================

/**
 * Lookup result interface
 */
export interface MDMLookupResult {
  /** Get display name for a UUID. Returns undefined if not found or loading. */
  getValue: (fielddefId: string, uuid: string) => string | undefined;

  /** Check if a specific value exists in the cache */
  hasValue: (fielddefId: string, uuid: string) => boolean;

  /** Direct access to lookup maps: Record<fielddefId, Map<uuid, name>> */
  lookups: Record<string, Map<string, string>>;

  /** True if any of the requested field definitions are still loading */
  isLoading: boolean;

  /** True if any of the requested field definitions failed to load */
  isError: boolean;

  /** Array of errors if any occurred */
  errors: Error[];
}

/**
 * Hook for quick MDM value lookups across multiple field definitions
 *
 * Purpose: Provides fast O(1) lookups for UUID → display name resolution
 * Architecture: Uses React Query to fetch and cache MDM values, builds lookup maps
 * Note: Only includes ACTIVE, non-deleted values in lookup maps (for display purposes)
 *
 * @param fielddefIds - Array of fielddef_ids to load (e.g., ['fd_organization', 'fd_currency'])
 * @param tenantId - Tenant context for the values
 * @returns MDMLookupResult with getValue function, loading state, and lookup maps
 *
 * @example
 * const { getValue, isLoading } = useMDMLookup(['fd_organization', 'fd_currency'], 'GLOBAL');
 *
 * // In renderCell:
 * renderCell: (value) => getValue('fd_organization', value) || value
 */
export const useMDMLookup = (fielddefIds: string[], tenantId: string): MDMLookupResult => {
  // Filter out empty/invalid fielddef IDs
  const validFielddefIds = useMemo(
    () => fielddefIds.filter((id) => id && id.length > 0),
    [fielddefIds]
  );

  // Fetch all requested field definitions in parallel
  // Uses same cache as useMDMValues (fetches ALL values including inactive/deleted)
  const queries = useQueries({
    queries: validFielddefIds.map((fielddefId) => ({
      queryKey: mdmCacheKeys.values(fielddefId, tenantId),
      queryFn: () =>
        mdmService.getValues({
          fielddef_id: fielddefId,
          tenant_id: tenantId,
          // Fetch ALL values - cache stores everything
          include_inactive: true,
          include_deleted: true,
        }),
      staleTime: CACHE_CONFIG.staleTime,
      gcTime: CACHE_CONFIG.gcTime,
      enabled: !!tenantId,
    })),
  });

  // Build lookup maps from query results (filter to ACTIVE values only for display)
  const lookups = useMemo(() => {
    const maps: Record<string, Map<string, string>> = {};

    validFielddefIds.forEach((fielddefId, index) => {
      const data = queries[index]?.data;
      const map = new Map<string, string>();

      if (data && Array.isArray(data)) {
        // Filter to only active, non-deleted values for display lookups
        const activeValues = filterActiveValues(data);
        activeValues.forEach((value: MasterDataValue) => {
          map.set(value.id, value.name);
        });
      }

      maps[fielddefId] = map;
    });

    return maps;
  }, [queries, validFielddefIds]);

  // Quick lookup function - O(1) performance with cross-map fallback for hierarchy support
  // When a value isn't found in the specific field's map (e.g., a hierarchical value stored
  // at a non-leaf level), falls back to searching all other loaded maps.
  const getValue = useCallback(
    (fielddefId: string, uuid: string): string | undefined => {
      if (!uuid) return undefined;
      // ORCH-418: the reserved ALL value is not a master-data value; it reads "All"
      if (uuid === ALL_VALUE) return ALL_LABEL;
      // Primary lookup: check the specific field's map
      const directResult = lookups[fielddefId]?.get(uuid);
      if (directResult) return directResult;
      // Fallback: search across all loaded maps (handles hierarchical field values
      // where the UUID belongs to an ancestor field)
      for (const key of Object.keys(lookups)) {
        if (key === fielddefId) continue;
        const fallbackResult = lookups[key]?.get(uuid);
        if (fallbackResult) return fallbackResult;
      }
      return undefined;
    },
    [lookups]
  );

  // Check if value exists
  const hasValue = useCallback(
    (fielddefId: string, uuid: string): boolean => {
      if (!uuid) return false;
      if (uuid === ALL_VALUE) return true;
      return lookups[fielddefId]?.has(uuid) ?? false;
    },
    [lookups]
  );

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const errors = queries.filter((q) => q.error).map((q) => q.error as Error);

  return {
    getValue,
    hasValue,
    lookups,
    isLoading,
    isError,
    errors,
  };
};

// =============================================================================
// CACHE INVALIDATION UTILITIES
// =============================================================================

/**
 * Hook providing cache invalidation methods
 *
 * Purpose: Allows components to invalidate MDM cache when values change
 * Architecture: Uses React Query's invalidateQueries for targeted invalidation
 */
export const useMDMCacheInvalidation = () => {
  const queryClient = useQueryClient();

  return {
    /**
     * Optimistically insert a newly-created value into the cache before the
     * background refetch resolves. Call this BEFORE onChange so the Autocomplete
     * can resolve the new id against options synchronously (ORCH-128 §7.3).
     */
    insertFielddefValue: (fielddefId: string, tenantId: string, value: MasterDataValue) => {
      queryClient.setQueryData<MasterDataValue[]>(
        mdmCacheKeys.values(fielddefId, tenantId),
        (prev) => (prev ? [...prev, value] : [value])
      );
      // Offer it at once in the scoped picker it was created from (ORCH-332). Scoped
      // lists for other permissions may not reach it; the caller's invalidateFielddef
      // refetches every scoped list right after, which settles that.
      queryClient.setQueriesData<MasterDataValue[]>(
        { queryKey: [...mdmCacheKeys.values(fielddefId, tenantId), 'scoped'] },
        (prev) => (prev ? [...prev, value] : prev)
      );
    },

    /**
     * Invalidate all MDM values for a specific field definition and tenant
     */
    invalidateFielddef: (fielddefId: string, tenantId: string) => {
      queryClient.invalidateQueries({
        queryKey: mdmCacheKeys.values(fielddefId, tenantId),
      });
    },

    /**
     * Invalidate all MDM values for a tenant
     */
    invalidateTenant: (tenantId: string) => {
      queryClient.invalidateQueries({
        queryKey: mdmCacheKeys.tenant(tenantId),
      });
    },

    /**
     * Invalidate a specific MDM value by ID
     */
    invalidateValue: (valueId: string) => {
      queryClient.invalidateQueries({
        queryKey: mdmCacheKeys.value(valueId),
      });
    },

    /**
     * Invalidate all MDM cache (use sparingly)
     */
    invalidateAll: () => {
      queryClient.invalidateQueries({
        queryKey: mdmCacheKeys.all(),
      });
    },

    /**
     * Prefetch MDM values for a field definition
     * Useful for preloading data before it's needed
     * Note: Fetches ALL values (including inactive/deleted) for cache consistency
     */
    prefetch: async (fielddefId: string, tenantId: string) => {
      await queryClient.prefetchQuery({
        queryKey: mdmCacheKeys.values(fielddefId, tenantId),
        queryFn: () =>
          mdmService.getValues({
            fielddef_id: fielddefId,
            tenant_id: tenantId,
            // Fetch ALL values - filtering happens client-side
            include_inactive: true,
            include_deleted: true,
          }),
        staleTime: CACHE_CONFIG.staleTime,
      });
    },
  };
};

// =============================================================================
// PRELOADING
// =============================================================================

/**
 * Hook to preload common MDM fields at app startup
 *
 * Purpose: Ensures frequently-used MDM values are cached before components need them
 * Usage: Call once in App.tsx or a top-level provider
 *
 * @param tenantId - The tenant context to preload for
 *
 * @example
 * // In App.tsx
 * const { currentTenantId } = useAuth();
 * useMDMPreload(currentTenantId);
 */
export const useMDMPreload = (tenantId: string | undefined | null) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    // Preload configured fields (fetches ALL values for cache consistency)
    CACHE_CONFIG.preloadFields.forEach((fielddefId) => {
      queryClient.prefetchQuery({
        queryKey: mdmCacheKeys.values(fielddefId, tenantId),
        queryFn: () =>
          mdmService.getValues({
            fielddef_id: fielddefId,
            tenant_id: tenantId,
            // Fetch ALL values - filtering happens client-side
            include_inactive: true,
            include_deleted: true,
          }),
        staleTime: CACHE_CONFIG.staleTime,
      });
    });
  }, [tenantId, queryClient]);
};

/**
 * Get the list of fields that are preloaded
 */
export const getPreloadedFields = (): readonly string[] => {
  return CACHE_CONFIG.preloadFields;
};

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  mdmCacheKeys,
  useMDMValues,
  useMDMValuesAll,
  useMDMValue,
  useMDMLookup,
  useMDMCacheInvalidation,
  useMDMPreload,
  getPreloadedFields,
  filterMDMValues,
  filterActiveValues,
};
